import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  MenuItem,
  FormHelperText,
  Icon,
} from "@mui/material";
import { debounce } from "lodash";
import { searchUser } from "../../DAL/fetch";
import { updateBanUser, updateUnBanUser } from "../../DAL/update";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { uploadFile } from "../../DAL/create";
import { fileUrl } from "../../Config/Config";

const BanUser = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [banned, setBanned] = useState("");
  const [userBan, setUserBan] = useState("");
  const [reason, setReason] = useState("");
  const [searchValue, setSearchValue] = useState();

  //for image upload
  const [fileURL1, setFileURL1] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [fileUploaded1, setFileUploaded1] = useState(false);
  const [formType, setFormType] = useState("Add");

  //time set
  const [hours, setHours] = useState("");
  const [seconds, setSeconds] = useState("");
  const [targetDate, setTargetDate] = useState(new Date().toISOString());

  const labelStyles = {
    color: "", // Default label color
    "&.Mui-focused": {
      color: "#8F29D0", // Focused label color (green)
    },
  };
  const inputStyles = {
    marginBottom: 2,
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#8F29D0", // Hover border color
      },
      "&.Mui-focused fieldset": {
        borderColor: "#8F29D0", // Focus border color (green)
      },
    },
  };

  const handleChange = (e) => {
    const value = e.target.value;

    if (/^\d*$/.test(value)) {
      setHours(value);

      if (value) {
        const secs = Number(value) * 3600; // hours -> seconds
        setSeconds(secs);

        const now = new Date();
        const futureDate = new Date(now.getTime() + secs * 1000);

        setTargetDate(futureDate.toISOString());
      } else {
        setSeconds(0);
        setTargetDate(null);
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile ? selectedFile.name : "");
    setFileUploaded1(false);
    setFilePreview(URL.createObjectURL(selectedFile));
  };
  const handleFileSubmit1 = async (e) => {
    e.preventDefault();
    if (!filePreview && formType === "Add") {
      setMessage(true);
      return;
    }
    if (!file) {
      setMessage(true);
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setLoading(true);
    try {
      const imagedata = await uploadFile(formData);
      setFileURL1(imagedata.data.url);
      setFileUploaded1(true);
      enqueueSnackbar("File uploaded successfully!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to upload file. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const searchData = async () => {
    setSearchLoading(true);
    try {
      const response = await searchUser({ codes: [searchTerm] });
      if (response?.data?.data || response?.data) {
        const userData = response.data.data || response.data;
        setData(userData);
        setBanned(userData[0]?.banned || false);
        setUserBan(userData[0]?.banned);
      } else {
        enqueueSnackbar("No use found.", { variant: "success" });
        setData([]);
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch user data.", { variant: "error" });
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search term update
  const debounceSearch = useCallback(
    debounce((query) => {
      setSearchTerm(query);
    }, 1000),
    []
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value.length <= 8) {
      debounceSearch(value);
    }
  };

  useEffect(() => {
    if (searchTerm.length === 8) {
      searchData();
    } else if (searchTerm.length <= 7) {
      searchData();
    }
  }, [searchTerm]);

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setSendLoading(true);

    if (userBan === "Normal_Banned" && !hours) {
      enqueueSnackbar("Hours are required for Normal Ban", {
        variant: "error",
      });
      setSendLoading(false);
      return;
    }

    try {
      let response;

      if (userBan === "Unbanned") {
        if (!data || data.length === 0) {
          throw new Error("No user data available");
        }
        response = await updateUnBanUser(String(data[0]?.code || ""));
      } else {
        const payload = {
          rid: String(searchValue),
          banType: userBan,
          time: String(targetDate), // always string
          reason: reason,
          proof: fileURL1,
        };
        response = await updateBanUser(payload);
      }

      if (response?.status === 200 || response?.status === 201) {
        enqueueSnackbar("User status updated successfully!", {
          variant: "success",
        });
        navigate(-1);
        setData([]);
        setSearchTerm("");
      } else {
        enqueueSnackbar(
          response?.message[0]?.constraints ||
            response?.error ||
            response?.message ||
            response?.data?.error ||
            response?.data?.message ||
            "Something went wrong",
          {
            variant: "error",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar("An error occurred while updating user status.", {
        variant: "error",
      });
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <>
      <ArrowBackIcon
        sx={{ margin: 1, cursor: "pointer" }}
        onClick={() => navigate(-1)}
      />
      <div className="wallet-area">
        <div className="Wallet-form">
          <Typography variant="h5">
            <b>Ban/Unban Users</b>
          </Typography>
          {data[0]?.name && (
            <>
              <Avatar
                alt={data[0]?.name}
                src={
                  data[0]?.picture?.startsWith("http")
                    ? data[0]?.picture
                    : fileUrl + data[0]?.picture
                }
                sx={{ height: "100px", width: "100px", margin: "10px" }}
              />
              <Typography variant="h5">{data[0]?.name}</Typography>
              <Typography variant="p">
                ({banned ? "Banned" : "Unbanned"})
              </Typography>
            </>
          )}
          <Box sx={{ marginBottom: 2, marginTop: 2 }}>
            <TextField
              fullWidth
              label="Search RID *"
              variant="outlined"
              size="small"
              value={searchValue}
              onChange={handleSearch}
              sx={inputStyles}
              InputLabelProps={{ sx: labelStyles }}
              disabled={searchLoading}
            />
            <TextField
              select
              fullWidth
              sx={inputStyles}
              required
              label="Status"
              value={userBan}
              InputLabelProps={{ sx: labelStyles }}
              onChange={(e) => setUserBan(e.target.value)}
              variant="outlined"
            >
              <MenuItem value="Unbanned">Un Bannned</MenuItem>
              <MenuItem value="Normal_Banned">Normal Banned</MenuItem>
              <MenuItem value="Device_Banned">Device Banned</MenuItem>
              <MenuItem value="Ip_Banned">IP Banned</MenuItem>
            </TextField>

            {userBan === "Normal_Banned" && (
              <>
                <TextField
                  fullWidth
                  label="Enter Hours"
                  variant="outlined"
                  value={hours}
                  onChange={handleChange}
                  required
                />
                <Typography variant="h6">Upload Proof</Typography>

                <Box sx={{ width: "100%", display: "flex", gap: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <Button
                    variant="contained"
                    color={fileUploaded1 ? "success" : "primary"}
                    onClick={handleFileSubmit1}
                    disabled={loading || !file}
                    sx={{ textTransform: "none" }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : fileUploaded1 ? (
                      "Uploaded"
                    ) : (
                      "Upload"
                    )}
                  </Button>
                </Box>
                {fileName && (
                  <Typography variant="body2">{fileName}</Typography>
                )}
                <FormHelperText sx={{ color: "red" }}>
                  {message && !filePreview && "Image is Required"}
                </FormHelperText>
                {filePreview && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={filePreview}
                      alt="Preview"
                      style={{ maxWidth: "100%", maxHeight: "200px" }}
                    />
                  </Box>
                )}

                <TextField
                  label="Enter Reason"
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={5}
                  fullWidth
                  sx={{ marginBottom: "10px" }}
                />
              </>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleUpdateStatus}
              className="gradientButton"
              disabled={sendLoading || searchLoading}
              sx={{ marginTop: 2, textTransform: "none" }}
            >
              {sendLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Update Status"
              )}
            </Button>
          </Box>
        </div>
      </div>
    </>
  );
};

export default BanUser;
